import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Card } from '../components/common/Card';
import { Modal } from '../components/common/Modal';
import { Button } from '../components/common/Button';
import {
  ShieldCheck,
  Users,
  UserPlus,
  Activity,
  Eye,
  Search,
  Calendar as CalendarIcon,
  X as XIcon
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency } from '../utils/formatters';

const PageContainer = styled.div`
  padding: 24px 20px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 10px;
  }
`;

const Header = styled.div`
  position: relative;
  padding: 22px 24px;
  margin-bottom: 18px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.06) 0%, rgba(79, 70, 229, 0.09) 100%);
  border: 1px solid rgba(99, 102, 241, 0.22);
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 6px 20px -8px rgba(99, 102, 241, 0.2);

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.22) 0%, transparent 65%);
    pointer-events: none;
  }

  .title-row {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .badge {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: linear-gradient(135deg, #4338CA 0%, #6366F1 100%);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px -2px rgba(99, 102, 241, 0.45);
    flex-shrink: 0;

    svg { width: 22px; height: 22px; stroke-width: 2.3; }
  }

  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: -1px;
    background: linear-gradient(135deg, #312E81 0%, #6366F1 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    position: relative;
    margin: 4px 0 0;
    font-size: 13.5px;
    color: #64748b;
    font-weight: 500;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 16px 14px;
    border-radius: 16px;

    h1 {
      font-size: 1.4rem;
      letter-spacing: -0.5px;
    }

    .badge { width: 32px; height: 32px; }
    .badge svg { width: 18px; height: 18px; }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-bottom: 18px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 14px;
  }
`;

const StatCard = styled.div`
  position: relative;
  padding: 16px 18px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 6px 18px -8px rgba(46, 91, 136, 0.12);
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 220ms ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => props.$accent || 'linear-gradient(180deg, #6366F1, #4338CA)'};
  }

  &::after {
    content: '';
    position: absolute;
    top: -30%;
    right: -15%;
    width: 140px;
    height: 140px;
    background: ${props => props.$glow || 'radial-gradient(circle, rgba(99,102,241,0.12), transparent 65%)'};
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-2px);
  }

  .icon-badge {
    width: 32px;
    height: 32px;
    border-radius: 9px;
    background: ${props => props.$accent || 'linear-gradient(135deg, #6366F1, #4338CA)'};
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
    box-shadow: 0 4px 10px -2px rgba(0, 0, 0, 0.12);

    svg { width: 16px; height: 16px; stroke-width: 2.3; }
  }

  h3 {
    font-size: 10.5px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    margin: 0 0 6px;
  }

  .value {
    font-size: 26px;
    font-weight: 800;
    letter-spacing: -0.8px;
    background: ${props => props.$gradient || 'linear-gradient(135deg, #312E81, #6366F1)'};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 10px;
    border-radius: 14px;

    .icon-badge {
      width: 26px;
      height: 26px;
      border-radius: 7px;
      margin-bottom: 6px;

      svg { width: 13px; height: 13px; }
    }

    h3 { font-size: 9px; letter-spacing: 0.3px; }
    .value { font-size: 18px; letter-spacing: -0.4px; }
  }
`;

const UsersTable = styled.div`
  position: relative;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 6px 20px -8px rgba(46, 91, 136, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #4338CA 0%, #6366F1 50%, #4338CA 100%);
    z-index: 2;
  }
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 22px 14px;
  background: transparent;
  color: #1e293b;

  .icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: linear-gradient(135deg, #6366F1 0%, #4338CA 100%);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 10px -2px rgba(99, 102, 241, 0.35);

    svg { width: 16px; height: 16px; stroke-width: 2.4; }
  }

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }

  .count {
    margin-left: auto;
    padding: 4px 10px;
    background: rgba(99, 102, 241, 0.1);
    color: #4338CA;
    border: 1px solid rgba(99, 102, 241, 0.25);
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    padding: 12px 14px;
    background: linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%);
    color: #475569;
    text-align: left;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    white-space: nowrap;
    border-bottom: 1px solid rgba(148, 163, 184, 0.18);
  }

  td {
    padding: 12px 14px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.14);
    font-size: 13px;
    color: #334155;
    font-weight: 500;
  }

  tbody tr {
    background: #ffffff;
    transition: background 140ms ease;
  }

  tbody tr:hover {
    background: rgba(99, 102, 241, 0.04);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 11.5px;

    th, td {
      padding: 8px 10px;
      font-size: 11px;
    }
  }
`;

const UnauthorizedMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px dashed rgba(239, 68, 68, 0.4);
  border-radius: 20px;

  h2 {
    margin: 12px 0 8px;
    color: #B91C1C;
    font-size: 22px;
    font-weight: 800;
  }

  p {
    color: #64748b;
    font-size: 14px;
    margin: 0;
  }

  .icon {
    width: 56px;
    height: 56px;
    margin: 0 auto;
    border-radius: 50%;
    background: linear-gradient(135deg, #FB7185 0%, #B91C1C 100%);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 24px -6px rgba(185, 28, 28, 0.4);

    svg { width: 26px; height: 26px; stroke-width: 2.2; }
  }
`;

const ViewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: linear-gradient(135deg, #6366F1 0%, #4338CA 100%);
  color: #ffffff;
  border: none;
  padding: 7px 12px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.25) inset,
    0 2px 6px -1px rgba(99, 102, 241, 0.35);
  transition:
    transform 140ms cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 180ms ease,
    filter 180ms ease;

  svg { width: 13px; height: 13px; stroke-width: 2.4; }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.3) inset,
      0 4px 10px -2px rgba(99, 102, 241, 0.45);
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SalesReportContainer = styled.div`
  max-height: 75vh;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.35);
    border-radius: 3px;
  }
`;

const ReportHeader = styled.div`
  position: relative;
  text-align: center;
  padding: 20px 20px 18px;
  margin-bottom: 18px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(79, 70, 229, 0.12) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 16px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 180px;
    height: 180px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.18), transparent 65%);
    pointer-events: none;
  }

  h2 {
    position: relative;
    margin: 0 0 6px;
    font-size: 1.35rem;
    font-weight: 800;
    letter-spacing: -0.5px;
    background: linear-gradient(135deg, #312E81, #6366F1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    position: relative;
    margin: 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 20px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
`;

const StatBox = styled.div`
  position: relative;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 14px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${props => props.$accent || 'linear-gradient(180deg, #6366F1, #4338CA)'};
  }

  .label {
    font-size: 10px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-bottom: 4px;
  }

  .value {
    font-size: 17px;
    font-weight: 800;
    letter-spacing: -0.4px;
    background: ${props => props.$gradient || 'linear-gradient(135deg, #312E81, #6366F1)'};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 10px;

    .label { font-size: 9px; }
    .value { font-size: 15px; }
  }
`;

const SalesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
  overflow: hidden;

  th {
    padding: 11px 12px;
    background: linear-gradient(135deg, #6366F1 0%, #4338CA 100%);
    color: #ffffff;
    text-align: left;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    white-space: nowrap;
  }

  td {
    padding: 10px 12px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.14);
    color: #334155;
    font-weight: 500;
  }

  tbody tr {
    background: #ffffff;
    transition: background 140ms ease;
  }

  tbody tr:hover {
    background: rgba(99, 102, 241, 0.04);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  .total {
    font-weight: 700;
    background: linear-gradient(135deg, #4338CA, #6366F1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-variant-numeric: tabular-nums;
  }
`;

const NoSalesMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px dashed rgba(148, 163, 184, 0.35);
  border-radius: 16px;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;

  svg {
    width: 48px;
    height: 48px;
    margin: 0 auto 12px;
    display: block;
    opacity: 0.55;
  }
`;

const FilterSection = styled.div`
  padding: 14px 16px;
  margin-bottom: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 2px 8px -4px rgba(46, 91, 136, 0.06);
`;

const FilterLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 10px;

  svg {
    width: 13px;
    height: 13px;
    color: #6366F1;
    stroke-width: 2.4;
  }
`;

const QuickFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
`;

const QuickFilterButton = styled.button`
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 600;
  border-radius: 999px;
  cursor: pointer;
  transition:
    background 180ms ease,
    color 180ms ease,
    border-color 180ms ease,
    transform 140ms cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 180ms ease;

  ${props => props.active ? `
    background: linear-gradient(135deg, #6366F1 0%, #4338CA 100%);
    color: #ffffff;
    border: 1px solid transparent;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.25) inset,
      0 3px 10px -2px rgba(99, 102, 241, 0.4);
  ` : `
    background: #ffffff;
    color: #475569;
    border: 1px solid rgba(148, 163, 184, 0.28);

    &:hover {
      background: rgba(99, 102, 241, 0.08);
      color: #4338CA;
      border-color: rgba(99, 102, 241, 0.3);
    }
  `}

  &:active {
    transform: scale(0.96);
  }
`;

const DateInputsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
  min-width: 140px;

  label {
    font-size: 10.5px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  input {
    padding: 10px 12px;
    border: 1px solid rgba(148, 163, 184, 0.28);
    border-radius: 10px;
    background: #ffffff;
    font-size: 13.5px;
    color: #1e293b;
    font-weight: 500;
    transition: border-color 160ms ease, box-shadow 160ms ease;

    &:focus {
      outline: none;
      border-color: #6366F1;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18);
    }
  }
`;

const ApplyFilterButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 10px 18px;
  background: linear-gradient(135deg, #6366F1 0%, #4338CA 100%);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.25) inset,
    0 3px 10px -2px rgba(99, 102, 241, 0.4);
  transition:
    transform 140ms cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 180ms ease,
    filter 180ms ease;

  svg { width: 14px; height: 14px; stroke-width: 2.4; }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.3) inset,
      0 5px 14px -2px rgba(99, 102, 241, 0.5);
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ClearFilterButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 10px 16px;
  background: #ffffff;
  color: #475569;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 160ms ease,
    border-color 160ms ease,
    color 160ms ease,
    transform 140ms ease;

  svg { width: 14px; height: 14px; stroke-width: 2.4; }

  &:hover {
    background: rgba(99, 102, 241, 0.06);
    border-color: rgba(99, 102, 241, 0.35);
    color: #4338CA;
  }

  &:active {
    transform: scale(0.96);
  }
`;

const ActiveFilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.14), rgba(79, 70, 229, 0.2));
  color: #4338CA;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
`;

const ADMIN_EMAIL = 'nomoalex@hotmail.com';

export const AdminUsers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    monthlyUsers: 0,
    activeToday: 0
  });

  // Estados para el modal de ventas
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [allSalesData, setAllSalesData] = useState([]); // Todas las ventas sin filtrar
  const [salesStats, setSalesStats] = useState({
    totalSales: 0,
    totalAmount: 0,
    totalItems: 0,
    avgTicket: 0,
    totalCost: 0,
    totalProfit: 0
  });
  const [loadingSales, setLoadingSales] = useState(false);

  // Estados para filtros de fecha
  const [activeFilter, setActiveFilter] = useState('todo');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterLabel, setFilterLabel] = useState('');

  useEffect(() => {
    if (!user || user.email !== ADMIN_EMAIL) {
      navigate('/');
      return;
    }

    loadUsers();
  }, [user, navigate]);

  // Función para parsear fechas de Firestore
  const parseFirestoreDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      if (dateValue.toDate) {
        return dateValue.toDate();
      } else if (typeof dateValue === 'string') {
        return new Date(dateValue);
      } else if (dateValue instanceof Date) {
        return dateValue;
      }
    } catch {
      return null;
    }
    return null;
  };

  // Función para obtener rango de fechas según filtro
  const getDateRange = (filterType) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (filterType) {
      case 'hoy':
        return {
          start: today,
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
          label: 'Hoy'
        };
      case 'ayer':
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        return {
          start: yesterday,
          end: new Date(today.getTime() - 1),
          label: 'Ayer'
        };
      case 'semana':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return {
          start: weekStart,
          end: now,
          label: 'Esta semana'
        };
      case 'mes':
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
          start: monthStart,
          end: now,
          label: 'Este mes'
        };
      case 'todo':
      default:
        return { start: null, end: null, label: 'Todo el tiempo' };
    }
  };

  // Función para filtrar ventas por fecha
  const filterSalesByDate = (sales, startD, endD) => {
    if (!startD && !endD) return sales;

    return sales.filter(sale => {
      const saleDate = parseFirestoreDate(sale.createdAt);
      if (!saleDate) return false;

      if (startD && saleDate < startD) return false;
      if (endD && saleDate > endD) return false;

      return true;
    });
  };

  // Función para calcular estadísticas
  const calculateStats = (sales) => {
    let totalAmount = 0;
    let totalItems = 0;
    let totalCost = 0; // Costo total de los productos vendidos

    sales.forEach(sale => {
      totalAmount += parseFloat(sale.total) || 0;
      totalItems += (sale.items || []).reduce((sum, item) => sum + (item.quantity || 0), 0);
      totalCost += sale.calculatedCost || 0; // Sumar el costo calculado de cada venta
    });

    const totalProfit = totalAmount - totalCost;

    return {
      totalSales: sales.length,
      totalAmount,
      totalItems,
      avgTicket: sales.length > 0 ? totalAmount / sales.length : 0,
      totalCost, // Costo de los productos vendidos
      totalProfit // Ganancia real (ventas - costo)
    };
  };

  // Aplicar filtro rápido
  const applyQuickFilter = (filterType) => {
    setActiveFilter(filterType);
    const range = getDateRange(filterType);
    setFilterLabel(range.label);

    // Limpiar fechas personalizadas
    setStartDate('');
    setEndDate('');

    const filteredSales = filterSalesByDate(allSalesData, range.start, range.end);
    setSalesData(filteredSales);
    setSalesStats(calculateStats(filteredSales));
  };

  // Aplicar filtro personalizado
  const applyCustomFilter = () => {
    if (!startDate && !endDate) return;

    setActiveFilter('personalizado');

    const start = startDate ? new Date(startDate + 'T00:00:00') : null;
    const end = endDate ? new Date(endDate + 'T23:59:59') : null;

    let label = '';
    if (start && end) {
      label = `${format(start, 'dd/MM/yyyy')} - ${format(end, 'dd/MM/yyyy')}`;
    } else if (start) {
      label = `Desde ${format(start, 'dd/MM/yyyy')}`;
    } else if (end) {
      label = `Hasta ${format(end, 'dd/MM/yyyy')}`;
    }
    setFilterLabel(label);

    const filteredSales = filterSalesByDate(allSalesData, start, end);
    setSalesData(filteredSales);
    setSalesStats(calculateStats(filteredSales));
  };

  // Limpiar filtros
  const clearFilters = () => {
    setActiveFilter('todo');
    setStartDate('');
    setEndDate('');
    setFilterLabel('Todo el tiempo');
    setSalesData(allSalesData);
    setSalesStats(calculateStats(allSalesData));
  };

  const loadUserSales = async (userId, pharmacyName) => {
    setLoadingSales(true);
    setSelectedUser({ id: userId, pharmacyName });
    setShowSalesModal(true);

    // Resetear filtros
    setActiveFilter('todo');
    setStartDate('');
    setEndDate('');
    setFilterLabel('Todo el tiempo');

    try {
      // Cargar productos para obtener los costos
      const productsRef = collection(db, 'users', userId, 'products');
      const productsSnapshot = await getDocs(productsRef);
      const productsMap = {};
      productsSnapshot.forEach((doc) => {
        const productData = doc.data();
        productsMap[doc.id] = productData.cost || 0;
      });

      // Cargar ventas
      const salesRef = collection(db, 'users', userId, 'sales');
      const salesQuery = query(salesRef, orderBy('createdAt', 'desc'));
      const salesSnapshot = await getDocs(salesQuery);

      const sales = [];
      salesSnapshot.forEach((doc) => {
        const saleData = doc.data();

        // Calcular el costo total de la venta basándose en los productos
        let saleCost = 0;
        if (saleData.items && Array.isArray(saleData.items)) {
          saleData.items.forEach(item => {
            const productCost = productsMap[item.productId] || 0;
            saleCost += productCost * (item.quantity || 1);
          });
        }

        sales.push({
          id: doc.id,
          ...saleData,
          calculatedCost: saleCost // Agregar el costo calculado
        });
      });

      // Guardar todas las ventas, calcular estadísticas
      setAllSalesData(sales);
      setSalesData(sales);
      setSalesStats(calculateStats(sales));
    } catch (error) {
      console.error('Error loading data:', error);
      setAllSalesData([]);
      setSalesData([]);
      setSalesStats({ totalSales: 0, totalAmount: 0, totalItems: 0, avgTicket: 0, totalCost: 0, totalProfit: 0 });
    } finally {
      setLoadingSales(false);
    }
  };

  const closeSalesModal = () => {
    setShowSalesModal(false);
    setSelectedUser(null);
    setSalesData([]);
    setAllSalesData([]);
    setActiveFilter('todo');
    setStartDate('');
    setEndDate('');
    setFilterLabel('');
  };

  const formatSaleDate = (dateValue) => {
    if (!dateValue) return 'Sin fecha';
    try {
      let date;
      if (dateValue.toDate) {
        date = dateValue.toDate();
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else {
        date = dateValue;
      }
      return format(date, "dd/MM/yyyy HH:mm", { locale: es });
    } catch {
      return 'Fecha inválida';
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      console.log('Loading users from Firestore...');
      
      const usersRef = collection(db, 'users');
      // Primero intentemos sin ordenamiento para ver si hay datos
      const querySnapshot = await getDocs(usersRef);
      
      console.log('Query snapshot size:', querySnapshot.size);
      console.log('Query snapshot empty:', querySnapshot.empty);
      
      const usersData = [];
      const now = new Date();
      const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      let monthlyCount = 0;
      let todayCount = 0;
      
      querySnapshot.forEach((doc) => {
        console.log('Processing user document:', doc.id);
        const userData = doc.data();
        console.log('User data:', userData);
        
        // Manejar diferentes formatos de fecha
        let createdAtDate = null;
        if (userData.createdAt) {
          if (userData.createdAt.toDate) {
            createdAtDate = userData.createdAt.toDate();
          } else if (typeof userData.createdAt === 'string') {
            createdAtDate = new Date(userData.createdAt);
          } else if (userData.createdAt instanceof Date) {
            createdAtDate = userData.createdAt;
          }
        }
        
        let lastLoginDate = null;
        // Primero intentar con lastLogin
        if (userData.lastLogin) {
          if (userData.lastLogin.toDate) {
            lastLoginDate = userData.lastLogin.toDate();
          } else if (typeof userData.lastLogin === 'string') {
            lastLoginDate = new Date(userData.lastLogin);
          } else if (userData.lastLogin instanceof Date) {
            lastLoginDate = userData.lastLogin;
          }
        } 
        // Si no hay lastLogin, usar updatedAt como fallback
        else if (userData.updatedAt) {
          if (userData.updatedAt.toDate) {
            lastLoginDate = userData.updatedAt.toDate();
          } else if (typeof userData.updatedAt === 'string') {
            lastLoginDate = new Date(userData.updatedAt);
          } else if (userData.updatedAt instanceof Date) {
            lastLoginDate = userData.updatedAt;
          }
        }
        
        const userWithId = {
          id: doc.id,
          ...userData,
          createdAt: createdAtDate,
          lastLogin: lastLoginDate
        };
        
        usersData.push(userWithId);
        
        // Calcular estadísticas
        if (createdAtDate && createdAtDate >= monthAgo) {
          monthlyCount++;
        }
        if (lastLoginDate && lastLoginDate >= todayStart) {
          todayCount++;
        }
      });
      
      console.log('Total users loaded:', usersData.length);
      console.log('Users data:', usersData);
      
      // Ordenar por fecha de creación (más reciente primero)
      usersData.sort((a, b) => {
        const dateA = a.createdAt || new Date(0);
        const dateB = b.createdAt || new Date(0);
        return dateB - dateA;
      });
      
      setUsers(usersData);
      setStats({
        totalUsers: usersData.length,
        monthlyUsers: monthlyCount,
        activeToday: todayCount
      });
    } catch (error) {
      console.error('Error loading users:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.email !== ADMIN_EMAIL) {
    return (
      <PageContainer>
        <UnauthorizedMessage>
          <div className="icon"><ShieldCheck /></div>
          <h2>Acceso Denegado</h2>
          <p>No tienes permisos para ver esta página.</p>
        </UnauthorizedMessage>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>
          <p>Cargando usuarios...</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <div className="title-row">
          <div className="badge"><ShieldCheck /></div>
          <div>
            <h1>Panel de Administración</h1>
            <p>Gestión de usuarios registrados en NomoFarma</p>
          </div>
        </div>
      </Header>

      <StatsGrid>
        <StatCard
          $accent="linear-gradient(135deg, #60A5FA, #2563EB)"
          $glow="radial-gradient(circle, rgba(37,99,235,0.12), transparent 65%)"
          $gradient="linear-gradient(135deg, #1E40AF, #2563EB)"
        >
          <div className="icon-badge"><Users /></div>
          <h3>Total de Usuarios</h3>
          <div className="value">{stats.totalUsers}</div>
        </StatCard>
        <StatCard
          $accent="linear-gradient(135deg, #34D399, #059669)"
          $glow="radial-gradient(circle, rgba(5,150,105,0.14), transparent 65%)"
          $gradient="linear-gradient(135deg, #047857, #10B981)"
        >
          <div className="icon-badge"><UserPlus /></div>
          <h3>Registros Este Mes</h3>
          <div className="value">{stats.monthlyUsers}</div>
        </StatCard>
        <StatCard
          $accent="linear-gradient(135deg, #FCD34D, #D97706)"
          $glow="radial-gradient(circle, rgba(217,119,6,0.14), transparent 65%)"
          $gradient="linear-gradient(135deg, #B45309, #F59E0B)"
        >
          <div className="icon-badge"><Activity /></div>
          <h3>Activos Hoy</h3>
          <div className="value">{stats.activeToday}</div>
        </StatCard>
      </StatsGrid>

      <UsersTable>
        <TableHeader>
          <div className="icon"><Users /></div>
          <h2>Usuarios Registrados</h2>
          {users.length > 0 && <span className="count">{users.length}</span>}
        </TableHeader>
        {users.length > 0 ? (
          <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Nombre de Farmacia</th>
                <th>Encargado</th>
                <th>Dirección</th>
                <th>Ciudad</th>
                <th>Teléfono</th>
                <th>Fecha de Registro</th>
                <th>Último Acceso</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((userData) => (
                <tr key={userData.id}>
                  <td>{userData.email}</td>
                  <td>{userData.pharmacyName || 'Sin nombre'}</td>
                  <td>{userData.ownerName || userData.contactName || 'No especificado'}</td>
                  <td>{userData.businessAddress || 'No registrada'}</td>
                  <td>{userData.businessCity || 'No especificada'}</td>
                  <td>{userData.businessPhone || 'No registrado'}</td>
                  <td>
                    {userData.createdAt
                      ? format(userData.createdAt, "dd 'de' MMMM 'de' yyyy", { locale: es })
                      : 'No disponible'}
                  </td>
                  <td>
                    {userData.lastLogin
                      ? format(userData.lastLogin, "dd/MM/yyyy HH:mm", { locale: es })
                      : 'Nunca'}
                  </td>
                  <td>
                    <ViewButton
                      onClick={() => loadUserSales(userData.id, userData.pharmacyName || userData.email)}
                    >
                      <Eye />
                      Ver Ventas
                    </ViewButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          </TableWrap>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            <p>No se encontraron usuarios registrados.</p>
            <p style={{ fontSize: '14px', marginTop: '10px' }}>
              Los usuarios aparecerán aquí cuando se registren en el sistema.
            </p>
          </div>
        )}
      </UsersTable>

      {/* Modal de Reporte de Ventas */}
      <Modal
        isOpen={showSalesModal}
        onClose={closeSalesModal}
        title={`Reporte de Ventas - ${selectedUser?.pharmacyName || ''}`}
        size="large"
      >
        <SalesReportContainer>
          {loadingSales ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>Cargando ventas...</p>
            </div>
          ) : (
            <>
              <ReportHeader>
                <h2>{selectedUser?.pharmacyName}</h2>
                <p>
                  Resumen de ventas
                  {filterLabel && <ActiveFilterBadge>{filterLabel}</ActiveFilterBadge>}
                </p>
              </ReportHeader>

              {/* Filtros de fecha */}
              <FilterSection>
                <FilterLabel>
                  <CalendarIcon />
                  Filtrar por fecha
                </FilterLabel>
                <QuickFilters>
                  <QuickFilterButton
                    active={activeFilter === 'todo'}
                    onClick={() => applyQuickFilter('todo')}
                  >
                    Todo
                  </QuickFilterButton>
                  <QuickFilterButton
                    active={activeFilter === 'hoy'}
                    onClick={() => applyQuickFilter('hoy')}
                  >
                    Hoy
                  </QuickFilterButton>
                  <QuickFilterButton
                    active={activeFilter === 'ayer'}
                    onClick={() => applyQuickFilter('ayer')}
                  >
                    Ayer
                  </QuickFilterButton>
                  <QuickFilterButton
                    active={activeFilter === 'semana'}
                    onClick={() => applyQuickFilter('semana')}
                  >
                    Esta semana
                  </QuickFilterButton>
                  <QuickFilterButton
                    active={activeFilter === 'mes'}
                    onClick={() => applyQuickFilter('mes')}
                  >
                    Este mes
                  </QuickFilterButton>
                </QuickFilters>

                <DateInputsRow>
                  <DateInputGroup>
                    <label>Desde</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </DateInputGroup>
                  <DateInputGroup>
                    <label>Hasta</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </DateInputGroup>
                  <ApplyFilterButton
                    onClick={applyCustomFilter}
                    disabled={!startDate && !endDate}
                  >
                    <Search />
                    Aplicar
                  </ApplyFilterButton>
                  {activeFilter !== 'todo' && (
                    <ClearFilterButton onClick={clearFilters}>
                      <XIcon />
                      Limpiar
                    </ClearFilterButton>
                  )}
                </DateInputsRow>
              </FilterSection>

              <StatsRow>
                <StatBox
                  $accent="linear-gradient(180deg, #34D399, #059669)"
                  $gradient="linear-gradient(135deg, #047857, #10B981)"
                >
                  <div className="label">Total Ventas</div>
                  <div className="value">{formatCurrency(salesStats.totalAmount)}</div>
                </StatBox>
                <StatBox
                  $accent="linear-gradient(180deg, #FCD34D, #D97706)"
                  $gradient="linear-gradient(135deg, #B45309, #F59E0B)"
                >
                  <div className="label">Costo Productos</div>
                  <div className="value">{formatCurrency(salesStats.totalCost)}</div>
                </StatBox>
                <StatBox
                  $accent={salesStats.totalProfit >= 0
                    ? 'linear-gradient(180deg, #60A5FA, #2563EB)'
                    : 'linear-gradient(180deg, #FB7185, #B91C1C)'}
                  $gradient={salesStats.totalProfit >= 0
                    ? 'linear-gradient(135deg, #1E40AF, #2563EB)'
                    : 'linear-gradient(135deg, #B91C1C, #EF4444)'}
                >
                  <div className="label">Ganancia Neta</div>
                  <div className="value">{formatCurrency(salesStats.totalProfit)}</div>
                </StatBox>
              </StatsRow>

              <StatsRow>
                <StatBox
                  $accent="linear-gradient(180deg, #94A3B8, #475569)"
                  $gradient="linear-gradient(135deg, #334155, #64748B)"
                >
                  <div className="label">Núm. Ventas</div>
                  <div className="value">{salesStats.totalSales}</div>
                </StatBox>
                <StatBox
                  $accent="linear-gradient(180deg, #94A3B8, #475569)"
                  $gradient="linear-gradient(135deg, #334155, #64748B)"
                >
                  <div className="label">Productos Vendidos</div>
                  <div className="value">{salesStats.totalItems}</div>
                </StatBox>
                <StatBox
                  $accent="linear-gradient(180deg, #A855F7, #7E22CE)"
                  $gradient="linear-gradient(135deg, #6B21A8, #A855F7)"
                >
                  <div className="label">Ticket Promedio</div>
                  <div className="value">{formatCurrency(salesStats.avgTicket)}</div>
                </StatBox>
              </StatsRow>

              {salesData.length > 0 ? (
                <>
                  <h3 style={{ marginBottom: '15px', color: '#333' }}>
                    Últimas ventas ({salesData.length})
                  </h3>
                  <SalesTable>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Fecha</th>
                        <th>Cliente</th>
                        <th>Productos</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesData.slice(0, 50).map((sale, index) => (
                        <tr key={sale.id}>
                          <td>{sale.saleNumber || index + 1}</td>
                          <td>{formatSaleDate(sale.createdAt)}</td>
                          <td>{sale.customerName || 'Público General'}</td>
                          <td>{(sale.items || []).length} productos</td>
                          <td className="total">{formatCurrency(sale.total || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </SalesTable>
                  {salesData.length > 50 && (
                    <p style={{ textAlign: 'center', marginTop: '15px', color: '#666', fontSize: '14px' }}>
                      Mostrando las últimas 50 de {salesData.length} ventas
                    </p>
                  )}
                </>
              ) : (
                <NoSalesMessage>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p>Esta farmacia aún no tiene ventas registradas.</p>
                </NoSalesMessage>
              )}
            </>
          )}
        </SalesReportContainer>
      </Modal>
    </PageContainer>
  );
};